import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { BillingService } from '../services/billing.service';
import { BillingDocument, BillingDocumentGroup } from '../models/billing-document.models';

@Component({
  selector: 'app-account-settings-billing',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NzButtonModule,
    NzCardModule,
    NzEmptyModule,
    NzListModule,
    NzSpinModule,
    NzTagModule,
    NzAlertComponent,
  ],
  templateUrl: './account-settings-billing.component.html',
  styleUrl: './account-settings-billing.component.less',
})
export class AccountSettingsBillingComponent implements OnInit {
  isLoading = true;
  paidPlanRequired = false;
  documentsByYear: BillingDocumentGroup[] = [];
  pendingUpgradeInfo: { plan?: string; contractStartDate?: string } | null = null;

  constructor(
    private billingService: BillingService,
    private message: NzMessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadDocuments();
  }

  get hasDocuments(): boolean {
    return this.documentsByYear.some((group) => group.documents.length > 0);
  }

  trackBillingDocumentGroup(_: number, group: BillingDocumentGroup): number {
    return Number(group.year);
  }

  trackBillingDocument(_: number, document: BillingDocument): string {
    return document.id;
  }

  download(document: BillingDocument): void {
    if (document.migratedWithoutFile || !document.hasFile) {
      return;
    }

    this.billingService.getMyBillingDocumentDownloadUrl(document.id).subscribe({
      next: ({ url }) => window.open(url, '_blank', 'noopener'),
      error: (error) => this.message.error(error?.message || 'Download failed'),
    });
  }

  statusColor(status: string): string {
    switch (status) {
      case 'active':
      case 'paid':
        return 'green';
      case 'expired':
      case 'overdue':
        return 'red';
      case 'issued':
        return 'blue';
      case 'draft':
        return 'gold';
      case 'cancelled':
      default:
        return 'default';
    }
  }

  formatBillingAmount(value?: number | null, currency = 'EUR'): string {
    const locale = (this.translate.currentLang || 'en').toLowerCase().startsWith('de') ? 'de-DE' : 'en';
    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0))} ${currency}`;
  }

  formatBillingDate(value?: string | Date | null): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    const isGerman = (this.translate.currentLang || '').toLowerCase().startsWith('de');
    return new Intl.DateTimeFormat(isGerman ? 'de-DE' : 'en', {
      day: isGerman ? '2-digit' : 'numeric',
      month: isGerman ? '2-digit' : 'short',
      year: 'numeric',
    }).format(date);
  }

  get pendingActivationText(): string | null {
    if (!this.pendingUpgradeInfo?.contractStartDate) { return null; }
    const date = this.formatBillingDate(this.pendingUpgradeInfo.contractStartDate);
    return this.translate.instant('ACCOUNT_SETTINGS.BILLING.PENDING_ACTIVATION', { date });
  }

  private loadDocuments(): void {
    this.isLoading = true;
    this.billingService.getMyBillingDocuments()
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe({
        next: (response: any) => {
          this.paidPlanRequired = !!response.paidPlanRequired;
          this.documentsByYear = this.sortDocumentGroups(response.documentsByYear || []);
          this.pendingUpgradeInfo = response.pendingUpgradeInfo || null;
        },
        error: () => {
          this.paidPlanRequired = false;
          this.documentsByYear = [];
        },
      });
  }

  private sortDocumentGroups(groups: BillingDocumentGroup[]): BillingDocumentGroup[] {
    return [...groups]
      .sort((a, b) => Number(b.year) - Number(a.year))
      .map((group) => ({
        year: group.year,
        documents: [...(group.documents || [])].sort((a, b) => this.compareBillingDocuments(a, b)),
      }));
  }

  private compareBillingDocuments(a: BillingDocument, b: BillingDocument): number {
    const issueDateDifference = this.getSortableTimestamp(b.issueDate) - this.getSortableTimestamp(a.issueDate);
    if (issueDateDifference !== 0) {
      return issueDateDifference;
    }

    const createdAtDifference = this.getSortableTimestamp(b.createdAt) - this.getSortableTimestamp(a.createdAt);
    if (createdAtDifference !== 0) {
      return createdAtDifference;
    }

    return (a.title || '').localeCompare(b.title || '');
  }

  private getSortableTimestamp(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
}
